import { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import { Fetching, globalActions } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';

import Body from './Body';
import Header from './Header';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { When } from 'react-if';

const EMPTY_ARRAY = [];
const REQUEST_ID = "Details_Statuses"
const useSearchStatuses = ({ jobId, getQueryBuilder, shouldBeLimited }) =>
  useQuery({
    ...StatusesQuery.QUERIES.search(
      getQueryBuilder, 
      { 
        query: "*", 
        size: 1,
        jobId,
        REQUEST_ID
      }
    ),
    initialPageParam: 0,
    refetchInterval: () => shouldBeLimited() ? undefined : 5000,
    getNextPageParam: CommonService.getNextPageParam
  })
const StatusDetails = () => {
  const dispatch = useDispatch(),
        jobId = useSelector(state => state.statusesState.jobId),
        // Set when the run is shown as a formula's: the header then names the formula, and its last runs and link are the formula's.
        formula = useSelector(state => state.statusesState.formula),
        queryClient = useQueryClient(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data, isPending } = useSearchStatuses({ jobId, getQueryBuilder, shouldBeLimited }),
        statuses = (data?.items?.[0] || EMPTY_ARRAY),
        reversed = useMemo(() => statuses?.componentStatuses?.slice(0).reverse() || EMPTY_ARRAY, [statuses]),
        fileName = useMemo(() => {
          if(statuses?.type !== StatusesEnums.STATUS_TYPE.ETL) {
            return statuses?.name
          } else {
            return statuses?.componentStatuses ? (statuses.componentStatuses.find(componentStatuses => componentStatuses.component === "Datalake")?.payload?.fileName || "No file name found") : ""
          }
        }, [statuses]),
        getNonETLId = useCallback(statuses => statusesActions.getNonETLId(statuses.payload, statuses.componentStatuses), [])

  useEffect(() => () => {
    dispatch(statusesActions.changeValue(undefined, "jobId"))
  }, [dispatch])

  useEffect(() => () => {
    queryClient.cancelQueries({ queryKey: [StatusesQuery.KEYS.SEARCH, REQUEST_ID] })
  }, [queryClient])

  return (
    <div className="statuses__content statuses__details">
      <When condition={!isPending}>
        <Header
            fileName={formula ? formula.name : fileName}
            formula={formula}
            id={getNonETLId(statuses)}
            name={statuses.name}
            payload={statuses.payload}
            startTime={statuses.startTime}
            type={statuses.type}
            version={formula ? formula.version : statuses.payload?.version}
        />
        <Body
            isRunning={statuses?.latestStatus?.status === StatusesEnums.STATUS.RUNNING || statuses?.latestStatus?.status === StatusesEnums.STATUS.PAUSED}
            statuses={reversed}
        />
      </When>
      <When condition={isPending}>
        <Fetching/>
      </When>
    </div>
  )
}

export default memo(StatusDetails);